import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';
import LoginComponent from '../pages/login/login';
import SetupFlow from '../pages/registration/Home';
import WithMobileViewLayout from '../HOC/layout/withMobileViewLayout';
import useLoadUser from '../hooks/shared/useLoadUser';
import ProtectedRoute from './ProtectedRoute';
import { SnackbarProvider } from '../provider/SnackbarProvider';
import { AppSnackbar } from '../components/shared/snackbar/AppSnackBar';
import Task from '../pages/task';
import MedicalHistory from '../pages/task/medicalHistory';
import HospitalExperience from '../pages/task/hospitalExperience';
import DailyLiving from '../pages/task/dailyLiving';
import LifeStyle from '../pages/task/lifeStyle';
import FamilyHistory from '../pages/task/familyHistory';
import Allergy from '../pages/allergies/list';
import Medication from '../pages/medications';
import RegistrationContainer from '../pages/registration';
import SurgeryInfo from '../pages/surgeryInfo';
import HowAreYouFeeling from '../pages/dashboard/howAreYouFeeling';
import ExploreEyeOps from '../pages/registration/onboarding/explore/exploreEyeOps';
import ForgotPassword from '../pages/registration/password/forgotPassword';
import EmailAlreadyRegistered from '../pages/registration/email/EmailAlreadyRegistered';
import IntroductoryCarouselContainer from '../pages/registration/onboarding/intro';
import Dashboard from '../pages/dashboard';
import ProfileUI from '../pages/profile/profileUI';
import ProfileDetailsComponent from '../pages/profile/ProfileDetailsComponent';
import NotificationsComponent from '../pages/profile/NotificationComponent';
import SettingsPage from '../pages/profile/SettingsComponent';
import WeeklyFeelingSurveyTracker from '../pages/dashboard/feelingSurvey/WeeklyFeelingSurveyTracker';
import TermsAndConditions from '../pages/profile/TermsAndConditions';
import { useLoading } from '../context/LoadingContext';
import Loader from '../components/shared/Loader';
import { useAppSelector } from '../redux/hooks';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect } from 'react';
import InvalidEmailComponent from '../pages/registration/email/InvalidEmailComponent';
import {
  MEDICAL_CONSISTENCY_PATH,
  PROFILE_ROUTES,
  ROUTES,
} from '../constants/paths';
import MedicalTracker from '../pages/dashboard/medicationConsistency/medicalTracker';
import PaymentHistoryListing from '../pages/profile/PaymentHistory/PaymentHistoryListing';
import PaymentDetails from '../pages/profile/PaymentHistory/PaymentDetails';

const { PROFILE } = ROUTES;
const { PAYMENT_HISTORY, PAYMENT_DETAILS } = PROFILE_ROUTES;

const routeConfig = [
  { path: '/', element: <RegistrationContainer /> },
  { path: '/setup/*', element: <SetupFlow /> },
  { path: '/auth/email-retry', element: <InvalidEmailComponent /> },
  { path: '/login', element: <LoginComponent /> },
  { path: '/profile', element: <ProfileUI />, protected: true },
  {
    path: '/profile/details',
    element: <ProfileDetailsComponent />,
    protected: true,
  },

  {
    path: '/profile/notification',
    element: <NotificationsComponent />,
    protected: true,
  },

  { path: '/profile/settings', element: <SettingsPage />, protected: true },
  {
    path: PROFILE + PAYMENT_HISTORY,
    element: <PaymentHistoryListing />,
    protected: true,
  },

  {
    path: PROFILE + PAYMENT_DETAILS + '/:id',
    element: <PaymentDetails />,
    protected: true,
  },
  {
    path: '/profile/terms-and-conditions',
    element: <TermsAndConditions />,
    protected: true,
  },
  { path: '/profile/edit', element: <ProfileUI />, protected: true },
  { path: '/surgery-info/:id', element: <SurgeryInfo />, protected: true },

  {
    path: '/how-are-you-feeling',
    element: <HowAreYouFeeling />,
    protected: true,
  },
  {
    path: MEDICAL_CONSISTENCY_PATH,
    element: <MedicalTracker />,
  },
  { path: '/registered-email', element: <EmailAlreadyRegistered /> },
  { path: '/dashboard', element: <Dashboard />, protected: true },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/explore',
    element: <ExploreEyeOps />,
    protected: true,
    firstTimeLoggedInUser: true,
  },
  {
    path: '/carousel',
    element: <IntroductoryCarouselContainer />,
    protected: true,
    firstTimeLoggedInUser: true,
  },
  { path: '/tasks', element: <Task /> },
  { path: '/tasks/medical', element: <MedicalHistory /> },
  { path: '/tasks/family', element: <FamilyHistory /> },
  { path: '/tasks/lifestyle', element: <LifeStyle /> },
  { path: '/tasks/hospital_experience', element: <HospitalExperience /> },
  { path: '/tasks/daily_living', element: <DailyLiving /> },
  { path: '/tasks/allergies', element: <Allergy /> },
  { path: '/tasks/medications', element: <Medication /> },
  { path: '/survey/track', element: <WeeklyFeelingSurveyTracker /> },
];

const AppRoutes = WithMobileViewLayout(() => {
  const { isLoading } = useLoadUser();

  const isFirstTimeUser = useAppSelector(
    (state) => state.auth.currentUser?.patientPlatformFirstLogin
  );

  const { setLoading } = useLoading();
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  useEffect(() => {
    setLoading(!!(isFetching || isMutating));
  }, [isFetching, isMutating, setLoading]);

  if (isLoading) return null;

  return (
    <>
      <Loader />
      <SnackbarProvider>
        <Router>
          <Routes>
            {routeConfig.map(
              ({
                path,
                element,
                protected: isProtected,
                firstTimeLoggedInUser,
              }) => {
                // Redirect to dashboard if it's for first-time users only and the user is not a first-time user
                if (firstTimeLoggedInUser && !isFirstTimeUser) {
                  return (
                    <Route
                      key={path}
                      path="*"
                      element={<Navigate to="/dashboard" replace />}
                    />
                  );
                }

                return (
                  <Route
                    key={path}
                    path={path}
                    element={
                      isProtected ? (
                        <ProtectedRoute>{element}</ProtectedRoute>
                      ) : (
                        element
                      )
                    }
                  />
                );
              }
            )}
          </Routes>
        </Router>
        <AppSnackbar />
      </SnackbarProvider>
    </>
  );
});

export default AppRoutes;
